<div class="settings-container">
  <div class="settings-header">
    <h1>Settings</h1>
    <p class="subtitle">Manage audit questions and system configuration</p>
  </div>

  <!-- Audit Questions CSV Upload Section -->
  <div class="section">
    <h2 class="section-title">Audit questions CSV upload</h2>
    <p class="section-description">
      Upload custom CSV files to override default audit questions. Each audit type supports dynamic categories.
    </p>

    <div class="audit-cards-grid">
      @for (card of auditCards; track card.type) {
        <mat-card class="audit-card">
          <mat-card-header>
            <mat-card-title>{{ card.title }}</mat-card-title>
            <mat-card-subtitle>{{ card.description }}</mat-card-subtitle>
          </mat-card-header>

          <mat-card-content>
            <!-- File Info Display -->
            @if (card.info) {
              <div class="file-info">
                <div class="info-row">
                  <span class="info-label">File Type:</span>
                  <span class="file-type-badge" [class.custom]="card.info.file_type === 'custom'">
                    {{ getFileTypeLabel(card) }}
                  </span>
                </div>
                <div class="info-row">
                  <span class="info-label">Questions:</span>
                  <span class="info-value">{{ card.info.row_count }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Categories:</span>
                  <span class="info-value">{{ card.info.category_count }}</span>
                </div>
                @if (card.info.categories.length > 0) {
                  <div class="categories-list">
                    <span class="info-label">Categories:</span>
                    <div class="categories-tags">
                      @for (category of card.info.categories; track category) {
                        <span class="category-tag">{{ category }}</span>
                      }
                    </div>
                  </div>
                }
              </div>
            }

            <!-- File Upload -->
            <div class="upload-section">
              <input
                type="file"
                [id]="'file-input-' + card.type"
                accept=".csv"
                (change)="onFileSelected($event, card)"
                style="display: none"
                #fileInput
              />
              
              <div class="file-input-wrapper">
                <button
                  mat-raised-button
                  color="primary"
                  (click)="fileInput.click()"
                  [disabled]="card.uploading || card.downloading"
                >
                  <mat-icon>upload_file</mat-icon>
                  Choose CSV File
                </button>
                
                @if (card.selectedFile) {
                  <span class="selected-file-name">{{ card.selectedFile.name }}</span>
                }
              </div>

              <div class="action-buttons">
                <button
                  mat-raised-button
                  color="accent"
                  (click)="uploadFile(card)"
                  [disabled]="!card.selectedFile || card.uploading || card.downloading"
                >
                  @if (card.uploading) {
                    <ng-container>
                      <mat-spinner diameter="20" style="display: inline-block; margin-right: 8px;"></mat-spinner>
                      Uploading...
                    </ng-container>
                  } @else {
                    <ng-container>
                      <mat-icon>cloud_upload</mat-icon>
                      Upload
                    </ng-container>
                  }
                </button>

                <button
                  mat-raised-button
                  color="primary"
                  (click)="downloadFile(card)"
                  [disabled]="card.uploading || card.downloading"
                >
                  @if (card.downloading) {
                    <ng-container>
                      <mat-spinner diameter="20" style="display: inline-block; margin-right: 8px;"></mat-spinner>
                      Downloading...
                    </ng-container>
                  } @else {
                    <ng-container>
                      <mat-icon>download</mat-icon>
                      Download
                    </ng-container>
                  }
                </button>
              </div>
            </div>
          </mat-card-content>
        </mat-card>
      }
    </div>
  </div>

  <!-- API Key Management Section -->
  <div class="section">
    <h2 class="section-title">API Key Management</h2>
    <p class="section-description">Manage API keys for external services. Keys are stored securely and can be updated by administrators.</p>

    @if (loadingKeys) {
      <div class="loading-container">
        <mat-spinner diameter="40"></mat-spinner>
        <p>Loading API keys...</p>
      </div>
    } @else {
      <div class="api-keys-grid">
        @for (key of apiKeys; track key.service_name) {
          <mat-card class="api-key-card">
            <mat-card-header>
              <mat-card-title>{{ getServiceName(key.service_name) }}</mat-card-title>
              <mat-card-subtitle>{{ getServiceDescription(key.service_name) }}</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content>
              <div class="api-key-info">
                <div class="info-row">
                  <span class="info-label">Status:</span>
                  <span class="status-badge" [class.active]="key.is_active" [class.inactive]="!key.is_active">
                    {{ key.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </div>
                <div class="info-row">
                  <span class="info-label">API Key:</span>
                  <span class="api-key-masked">{{ key.api_key_masked || 'Not set' }}</span>
                </div>
                @if (key.last_verified_at) {
                  <div class="info-row">
                    <span class="info-label">Last Verified:</span>
                    <span class="info-value">{{ key.last_verified_at | date:'short' }}</span>
                  </div>
                }
                @if (key.last_verification_status) {
                  <div class="info-row">
                    <span class="info-label">Last Status:</span>
                    <span class="status-badge" 
                          [class.working]="key.last_verification_status === 'working'"
                          [class.failed]="key.last_verification_status === 'failed'">
                      {{ key.last_verification_status === 'working' ? 'Working' : 'Failed' }}
                    </span>
                  </div>
                }
                @if (key.updated_at) {
                  <div class="info-row">
                    <span class="info-label">Last Updated:</span>
                    <span class="info-value">{{ key.updated_at | date:'short' }}</span>
                  </div>
                }
              </div>

              @if (key.service_name === 'oxylabs' && editingOxylabs) {
                <div class="edit-key-section">
                  <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Username</mat-label>
                    <input matInput type="text" [(ngModel)]="editingOxylabs.username" placeholder="Web search username">
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Password</mat-label>
                    <input matInput type="password" [(ngModel)]="editingOxylabs.password" placeholder="Web search password">
                  </mat-form-field>
                  <div class="edit-actions">
                    <button mat-raised-button color="primary" (click)="updateApiKey(key.service_name)">
                      <mat-icon>save</mat-icon>
                      Save Credentials
                    </button>
                    <button mat-button (click)="cancelEditingKey()">
                      <mat-icon>cancel</mat-icon>
                      Cancel
                    </button>
                  </div>
                </div>
              } @else if (editingKey && editingKey.serviceName === key.service_name) {
                <div class="edit-key-section">
                  <mat-form-field appearance="outline" class="full-width">
                    <mat-label>New API Key</mat-label>
                    <input matInput type="password" [(ngModel)]="editingKey.newKey" placeholder="Enter new API key">
                  </mat-form-field>
                  <div class="edit-actions">
                    <button mat-raised-button color="primary" (click)="updateApiKey(key.service_name)">
                      <mat-icon>save</mat-icon>
                      Save
                    </button>
                    <button mat-button (click)="cancelEditingKey()">
                      <mat-icon>cancel</mat-icon>
                      Cancel
                    </button>
                  </div>
                </div>
              } @else {
                <div class="action-buttons-row">
                  <button mat-raised-button color="primary" (click)="startEditingKey(key)" class="edit-button">
                    <mat-icon>edit</mat-icon>
                    @if (key.service_name === 'oxylabs') {
                      Update Credentials
                    } @else {
                      Update Key
                    }
                  </button>
                </div>
              }

              <!-- Service Management Link -->
              @if (getServiceUrl(key)) {
                <div class="service-management-section">
                  <a [href]="getServiceUrl(key)" target="_blank" rel="noopener noreferrer" class="management-link">
                    <mat-icon>open_in_new</mat-icon>
                    <span>Manage API Key & Check Status</span>
                  </a>
                </div>
              }

              <!-- Service Verification Section (for all services) -->
              @if (hasVerification(key.service_name)) {
                <div class="verification-section">
                  <div class="verification-status">
                    @if (getVerificationStatus(key.service_name)) {
                      <div class="status-indicator" 
                           [class.working]="getVerificationStatus(key.service_name) === 'working'"
                           [class.failed]="getVerificationStatus(key.service_name) === 'failed'">
                        <mat-icon>{{ 
                          getVerificationStatus(key.service_name) === 'working' 
                            ? 'check_circle' : 'error' 
                        }}</mat-icon>
                        <span>{{ 
                          getVerificationStatus(key.service_name) === 'working' 
                            ? 'Working' : 'Failed' 
                        }}</span>
                      </div>
                      @if (getVerificationMessage(key.service_name)) {
                        <p class="status-message">{{ getVerificationMessage(key.service_name) }}</p>
                      }
                      @if (getVerificationLastChecked(key.service_name)) {
                        <p class="last-checked">Last checked: {{ getVerificationLastChecked(key.service_name) | date:'short' }}</p>
                      }
                    } @else {
                      <p class="no-status">Not verified yet</p>
                    }
                  </div>
                  <button
                    mat-raised-button
                    color="accent"
                    (click)="verifyService(key.service_name)"
                    [disabled]="isVerifying(key.service_name)"
                    class="verify-button">
                    @if (isVerifying(key.service_name)) {
                      <mat-spinner diameter="20" style="display: inline-block; margin-right: 8px;"></mat-spinner>
                      Verifying...
                    } @else {
                      <ng-container>
                        <mat-icon>verified</mat-icon>
                        Verify Service
                      </ng-container>
                    }
                  </button>
                  <p class="verification-description">
                    {{ getVerificationDescription(key.service_name) }}
                  </p>
                </div>
              }
            </mat-card-content>
          </mat-card>
        }
      </div>
    }
  </div>

  <!-- Section reload button (admin only) -->
  @if (isAdmin) {
  <div class="section">
    <h2 class="section-title">Section reload (admin)</h2>
    <p class="section-description">
      When enabled, a reload button is shown on the AI Content Index chart so you can re-run only that section and update the current version without running the full bulk analysis.
    </p>
    <mat-card class="audit-card">
      <mat-card-content>
        @if (loadingUserSettings) {
          <p><mat-spinner diameter="24" style="display: inline-block; vertical-align: middle;"></mat-spinner> Loading...</p>
        } @else {
          <div class="setting-row">
            <mat-slide-toggle
              [checked]="showSectionReloadButton"
              [disabled]="savingSectionReloadSetting"
              (change)="onShowSectionReloadButtonChange($event.checked)">
              Show section reload button
            </mat-slide-toggle>
            @if (savingSectionReloadSetting) {
              <mat-spinner diameter="20" style="display: inline-block; margin-left: 8px;"></mat-spinner>
            }
          </div>
        }
      </mat-card-content>
    </mat-card>
  </div>
  }

  <!-- Cited Domains CSV (Settings) -->
  <!-- <div class="section" style="width: 32%;">
    <h2 class="section-title">Cited domains CSV</h2>
    <p class="section-description">
      Manage the cited domains table used for citations and recommended domains. Supports override and strict header validation.
    </p>

    <mat-card class="audit-card">
      <mat-card-header>
        <mat-card-title>Cited domains upload & download</mat-card-title>
        <mat-card-subtitle>Columns: Domain, Domain authority, Category</mat-card-subtitle>
      </mat-card-header>

      <mat-card-content>
        <div class="file-info">
          <div class="info-row">
            <span class="info-label">Mode:</span>
            <span class="file-type-badge custom">
              {{ citedDomains.override ? 'Override (truncate + insert)' : 'Upsert (merge by domain)' }}
            </span>
          </div>
          <div class="info-row">
            <span class="info-label">Validation:</span>
            <span class="file-type-badge" [class.custom]="citedDomains.strict">
              {{ citedDomains.strict ? 'Strict headers required' : 'Flexible headers' }}
            </span>
          </div>
        </div>

        <div class="upload-section">
          <input
            type="file"
            id="cited-file-input"
            accept=".csv"
            (change)="onCitedFileSelected($event)"
            style="display: none"
            #citedFileInput
          />

          <div class="file-input-wrapper">
            <button
              mat-raised-button
              color="primary"
              (click)="citedFileInput.click()"
              [disabled]="citedDomains.uploading || citedDomains.downloading"
            >
              <mat-icon>upload_file</mat-icon>
              Choose CSV File
            </button>

            @if (citedDomains.selectedFile) {
              <span class="selected-file-name">{{ citedDomains.selectedFile.name }}</span>
            }
          </div>

          <div class="toggle-row">
            <mat-slide-toggle color="primary" [(ngModel)]="citedDomains.override">
              Override (truncate then insert)
            </mat-slide-toggle>
            <mat-slide-toggle color="primary" [(ngModel)]="citedDomains.strict">
              Strict headers (exact columns)
            </mat-slide-toggle>
          </div>

          <div class="action-buttons">
            <button
              mat-raised-button
              color="accent"
              (click)="uploadCitedDomains()"
              [disabled]="!citedDomains.selectedFile || citedDomains.uploading || citedDomains.downloading"
            >
              @if (citedDomains.uploading) {
                <ng-container>
                  <mat-spinner diameter="20" style="display: inline-block; margin-right: 8px;"></mat-spinner>
                  Uploading...
                </ng-container>
              } @else {
                <ng-container>
                  <mat-icon>cloud_upload</mat-icon>
                  Upload
                </ng-container>
              }
            </button>

            <button
              mat-raised-button
              color="primary"
              (click)="downloadCitedDomains()"
              [disabled]="citedDomains.uploading || citedDomains.downloading"
            >
              @if (citedDomains.downloading) {
                <ng-container>
                  <mat-spinner diameter="20" style="display: inline-block; margin-right: 8px;"></mat-spinner>
                  Downloading...
                </ng-container>
              } @else {
                <ng-container>
                  <mat-icon>download</mat-icon>
                  Download
                </ng-container>
              }
            </button>
          </div>
        </div>
      </mat-card-content>
    </mat-card>
  </div> -->
</div>
